import React, { useEffect, useState } from "react";
import { api } from "@/lib/http";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Zap } from "lucide-react";
import { toast } from "sonner";

export const useDevices = () => {
  const [devices, setDevices] = useState([]);
  useEffect(() => { api.get("/devices").then(({ data }) => setDevices(data || [])).catch(() => {}); }, []);
  return devices;
};

// Pulses fired + which laser. Inline (checkout: value/onChange) or self-saving (appointment window: bookingId).
export const PulseCount = ({ value, onChange, deviceId, onDeviceChange, bookingId, booking, onSaved, compact }) => {
  const devices = useDevices();
  const [pulses, setPulses] = useState(booking?.pulses ?? "");
  const [dev, setDev] = useState(booking?.pulse_device_id || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (booking) { setPulses(booking.pulses ?? ""); setDev(booking.pulse_device_id || ""); } }, [booking?.id, booking?.pulses, booking?.pulse_device_id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (devices.length === 1 && !(deviceId ?? dev)) { onDeviceChange ? onDeviceChange(devices[0].id) : setDev(devices[0].id); } }, [devices]); // eslint-disable-line react-hooks/exhaustive-deps
  const controlled = typeof onChange === "function";
  const p = controlled ? value : pulses;
  const d = controlled ? deviceId : dev;
  const setP = (v) => (controlled ? onChange(v) : setPulses(v));
  const setD = (v) => (controlled ? onDeviceChange?.(v) : setDev(v));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/bookings/${bookingId}/pulses`, { pulses: parseInt(p || 0, 10), device_id: d || "" });
      toast.success(`${data.pulses.toLocaleString()} pulses saved${data.pulse_cost ? ` · machine cost $${data.pulse_cost.toFixed(2)}` : ""}`);
      onSaved?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save pulses"); }
    setSaving(false);
  };
  return (
    <div className={compact ? "flex flex-wrap items-center gap-2" : "space-y-1.5"} data-testid="pulse-count">
      {!compact && <label className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Zap className="h-3 w-3" />Pulses fired</label>}
      <div className="flex flex-wrap items-center gap-2">
        <Input type="number" inputMode="numeric" min="0" step="1" placeholder="Pulses" value={p ?? ""} onChange={(e) => setP(e.target.value)} className="h-9 rounded-lg w-28" data-testid="pulse-input" />
        {devices.length > 1 && (
          <Select value={d || ""} onValueChange={setD}>
            <SelectTrigger className="h-9 rounded-lg w-44" data-testid="pulse-device"><SelectValue placeholder="Which laser?" /></SelectTrigger>
            <SelectContent>{devices.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {devices.length === 1 && <span className="text-xs text-muted-foreground">{devices[0].name}</span>}
        {devices.length === 0 && <span className="text-xs text-muted-foreground">Save your laser in Tools → Laser calculator to cost each pulse</span>}
        {bookingId && <Button size="sm" variant="outline" className="rounded-lg h-9" onClick={save} disabled={saving || p === "" || p === null} data-testid="pulse-save">{saving ? "Saving…" : "Save"}</Button>}
      </div>
      {bookingId && booking?.pulses > 0 && <div className="text-[11px] text-muted-foreground" data-testid="pulse-summary">{booking.pulses.toLocaleString()} pulses{booking.pulse_device_name ? ` on ${booking.pulse_device_name}` : ""}{booking.pulse_cost ? ` · machine cost $${Number(booking.pulse_cost).toFixed(2)}` : ""}</div>}
    </div>
  );
};
